// Encrypted on-device SQLite (SQLCipher) foundation for Molaroo native local-first.
// NATIVE ONLY. Never imported/initialised on web. The DB key is a 256-bit per-install
// secret held in the OS secure store (iOS Keychain / Android Keystore) via
// @aparajita/capacitor-secure-storage — never in source, localStorage, logs or MongoDB.
import { Capacitor } from "@capacitor/core";

export const DB_NAME = "molaroo";
export const SCHEMA_VERSION = 2;
export const DB_KEY_NAME = "molaroo.db.key"; // secure-storage entry holding the SQLCipher passphrase

// Record collections mirrored locally (full-fidelity JSON; integer cents preserved exactly).
export const RECORD_TABLES = [
  "practices", "agreement_versions", "work_entries", "payments", "payment_reports",
  "adjustments", "expenses", "reserve_deposits", "settings", "accountant_profiles", "files",
];

export function newId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function randomHex(bytes) {
  const a = new Uint8Array(bytes);
  (globalThis.crypto || window.crypto).getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

// 256-bit (32-byte) cryptographically secure per-installation key, persisted only in
// the OS secure store. Generated once, then retrieved on subsequent launches.
export async function getOrCreateDbKey() {
  const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
  let key = null;
  try {
    const v = await SecureStorage.get(DB_KEY_NAME);
    if (typeof v === "string" && v.length >= 32) key = v;
  } catch { /* not set yet */ }
  if (!key) {
    key = randomHex(32); // 256-bit
    await SecureStorage.set(DB_KEY_NAME, key);
  }
  return key;
}

function ddl() {
  const stmts = [];
  for (const t of RECORD_TABLES) {
    stmts.push(
      `CREATE TABLE IF NOT EXISTS ${t} (id TEXT PRIMARY KEY, user_id TEXT, data TEXT NOT NULL, updated_at TEXT, pending INTEGER DEFAULT 0, srv_deleted INTEGER DEFAULT 0);`,
    );
    stmts.push(`CREATE INDEX IF NOT EXISTS idx_${t}_user ON ${t}(user_id);`);
  }
  stmts.push(
    `CREATE TABLE IF NOT EXISTS api_cache (url TEXT PRIMARY KEY, response TEXT NOT NULL, cached_at TEXT);`,
    `CREATE TABLE IF NOT EXISTS outbox (key TEXT PRIMARY KEY, method TEXT, url TEXT, body TEXT, meta TEXT, status TEXT DEFAULT 'pending', attempts INTEGER DEFAULT 0, last_error TEXT, created_at TEXT, updated_at TEXT);`,
    `CREATE TABLE IF NOT EXISTS migration_state (user_id TEXT PRIMARY KEY, seeded INTEGER DEFAULT 0, seeded_at TEXT, schema_version INTEGER);`,
    // v2 (data reconciliation): per-practice marker that its agreement_versions have been
    // authoritatively refreshed from the server. Absent = untrusted (offline new work -> Pending).
    `CREATE TABLE IF NOT EXISTS agreement_refresh (id TEXT PRIMARY KEY, user_id TEXT, refreshed_at TEXT);`,
  );
  return stmts.join("\n");
}

// Executor wraps a Capacitor SQLite connection behind a small, testable interface.
export function makeExecutor(conn) {
  return {
    async run(sql, values = []) { await conn.run(sql, values, false); },
    async all(sql, values = []) { const r = await conn.query(sql, values); return r.values || []; },
    async exec(sql) { await conn.execute(sql); },
    async transaction(fn) {
      // Use the plugin's native transaction control — NOT conn.execute("BEGIN"/"COMMIT").
      // conn.execute defaults to transaction:true, which wraps the statement in its OWN
      // transaction; issuing "BEGIN TRANSACTION" inside that wrapper nests a transaction and
      // fails with "cannot start a transaction within a transaction". Re-entrant calls run
      // inline so the OUTERMOST caller stays the single transaction owner (no nesting).
      const active = await conn.isTransactionActive();
      if (active && active.result) return fn();
      await conn.beginTransaction();
      try {
        const r = await fn();
        await conn.commitTransaction();
        return r;
      } catch (e) {
        try {
          const a = await conn.isTransactionActive();
          if (a && a.result) await conn.rollbackTransaction();
        } catch { /* ignore rollback failure */ }
        throw e;
      }
    },
  };
}

let _exec = null;
let _initPromise = null;

// Opens the encrypted database and applies the schema. Encryption is enabled via the
// CapacitorSQLite plugin config (iosIsEncryption/androidIsEncryption=true) and the
// connection is opened in mode "secret" with the passphrase supplied by setEncryptionSecret.
export async function initDb() {
  if (!Capacitor.isNativePlatform()) return null;
  if (_exec) return _exec;
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const { CapacitorSQLite, SQLiteConnection } = await import("@capacitor-community/sqlite");
    const sqlite = new SQLiteConnection(CapacitorSQLite);
    const key = await getOrCreateDbKey();
    const stored = await sqlite.isSecretStored();
    if (!stored.result) {
      // One-time: store our 256-bit key as the SQLCipher secret (Keychain/Keystore backed).
      await sqlite.setEncryptionSecret(key);
    }
    const isConn = (await sqlite.isConnection(DB_NAME, false)).result;
    const conn = isConn
      ? await sqlite.retrieveConnection(DB_NAME, false)
      : await sqlite.createConnection(DB_NAME, true, "secret", SCHEMA_VERSION, false);
    await conn.open();
    const exec = makeExecutor(conn);
    await exec.exec(ddl());
    _exec = exec;
    return exec;
  })();
  return _initPromise;
}

export function getExec() { return _exec; }
export function ddlForTests() { return ddl(); }
